/**
 * @file   src\components\LocationInfoForm.tsx
 * @brief  LocationInfo Form component.
 * @date   JUN, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */
import { Link, useState, useEffect, useRef, Row, Col, useParams } from '../utils/thirdpartyComponents';
import CWInput from '../components/CWInput';
import CWRadio from './CWRadio';
import CWSelect from './CWSelect';
import CWSwitch from './CWSwitch';
import { locationInfoSchema } from '../validations/locationInfoSchema';
import * as Yup from 'yup'
import { CITY_MAX_LENGTH, EMAIL_MAX_LENGTH, LOCATION_NAME_MAX_LENGTH, TIMEZONE_OFFSET_OPTIONS, WASH_ADDRESS_MAX_LENGTH } from '../utils/constants';
import { AddInfoFormProps, LocationData } from '../interfaces/generalInterface';
import { Option } from "../interfaces/stateInterface";
import { CountryItem } from "../interfaces/CarWashInterface";
import { FleetPaymentMode, userType } from '../utils/enums';
import { DEFAULT_COUNTRY_CODE, fetchCompanyAddressDetails, fetchStatesByCountry, fetchSupportedCountries, getCountryDisplayName, getCountryFieldLabels, getPostalCodeMaxLength, mapCountryOptions, normalizePostalCodeInput } from '../utils/countryState';
import { MessageToaster } from '../utils/toastUtil';
import LocationFleetSettings from './LocationFleetSettings';
import LocationFeatureToggles from './LocationFeatureToggles';
import { DEFAULT_LOCATION_FLEET_SETTINGS, getDisabledFleetSettings } from '../utils/locationFleetSettings';
import { DEFAULT_LOCATION_FEATURE_TOGGLES } from '../utils/locationFeatureSettings';

const LocationInfoForm: React.FC<AddInfoFormProps> = ({ onValidate, onDataChange, m_formData, edit, cuserType }) => {

    // get car wash id
    const param = useParams();
    const carwashid_param = param.id ? parseInt(param.id) : 0;

    const toaster = new MessageToaster();
    // State to handle options for country and state
    const [supportedCountries, setSupportedCountries] = useState<CountryItem[]>([]);
    const [options, setOptions] = useState<Option[]>([]);
    // State to company details
    const [company, setCompany] = useState({
        companyName:  "" ,
        address1:"" ,
        address2: "" ,
        city:  "" ,
        country: "",
        state:  "" ,
        zipCode:  ""
    });


    useEffect(() => {
        getCompany(); // company list
        getCountries();
        getSateList(DEFAULT_COUNTRY_CODE);
    }, []);

    const getCountries = async () => {
        try {
            const countries = await fetchSupportedCountries();
            setSupportedCountries(countries)
        } catch (error: any) {
            toaster.toastError(error.message);
        }
    }

    const getSateList = async (countryCode: string) => {
        try {
            const stateOptions = await fetchStatesByCountry(countryCode);
            setOptions(stateOptions);
        } catch (error: any) {
            setOptions([]);
            toaster.toastError(error.message);
        }
    }

    // To get company details
    const getCompany = async () => {
        try {
            const companyAddress = await fetchCompanyAddressDetails(cuserType === userType['CARWASH-OWNER'], carwashid_param);
            if (companyAddress) {
                setCompany({
                    ...companyAddress,
                    country: companyAddress.countryCode,
                    zipCode: companyAddress.zipCode,
                });
                setFormData((prev) => {
                    const next = {
                        ...prev,
                        country: companyAddress.countryCode,
                        ...(prev.hasDefaultAddress ? {
                            state: companyAddress.state || prev.state,
                        } : {}),
                    };
                    onDataChange(next);
                    return next;
                });
                getSateList(companyAddress.countryCode);
            }

        } catch (error: any) {
            throw new Error(error.response?.data?.message || error.message);
        }
    }

    //State to handle form data
    const [formData, setFormData] = useState<LocationData>({
        carWashId: carwashid_param,
        locationName: '',
        email: '',
        timezoneOffset: '',
        isComboWash: false,
        ...DEFAULT_LOCATION_FLEET_SETTINGS,
        ...DEFAULT_LOCATION_FEATURE_TOGGLES,
        hasDefaultAddress: true,
        address1: '',
        address2: '',
        city: '',
        country: DEFAULT_COUNTRY_CODE,
        state: '',
        zipcode: ''
    });

    // State to save location details that we get
    const [locationData, setLocationData] = useState<LocationData>(
        { carWashId: carwashid_param, locationName: "Location 1", email: "example.user@example.com", timezoneOffset: '', isComboWash: false, ...DEFAULT_LOCATION_FLEET_SETTINGS, ...DEFAULT_LOCATION_FEATURE_TOGGLES, hasDefaultAddress: true, address1: "123 Main St", address2: '', city: 'Anytown', country: DEFAULT_COUNTRY_CODE, state: 'Nebraska', zipcode: 'CA 12345' })
    const countryOptions = mapCountryOptions(supportedCountries);
    const selectedCountry = edit ? locationData.country : formData.country;
    const { stateLabel, postalLabel } = getCountryFieldLabels(supportedCountries, selectedCountry);
    const postalMaxLength = getPostalCodeMaxLength(selectedCountry);
    const companyCountryName = getCountryDisplayName(company.country);
    const locationCountryName = getCountryDisplayName(locationData.country);


    // State to handle errors
    const [errors, setErrors] = useState<any>({});

    const refs = useRef<{ [key: string]: HTMLInputElement | HTMLTextAreaElement | null }>({});

    // On input change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {

        if ((e.target.name === "zipcode")) {
            const country = edit ? locationData.country : formData.country;
            e.target.value = normalizePostalCodeInput(e.target.value, country);
        }

        const { name, value } = e.target;

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        if (edit) {
            setLocationData({
                ...locationData,
                [name]: value
            })
            onDataChange({
                ...locationData,
                [name]: value
            });
        } else {
            const newFormData = {
                ...formData,
                [name]: value,
            };

            setFormData(newFormData);
            onDataChange(newFormData);
        }

    };


    //To validate form on clicking next
    const validateForm = async (): Promise<boolean> => {
        try {
            if (edit) {
                await locationInfoSchema.validate(locationData, { abortEarly: false });

            } else {
                await locationInfoSchema.validate(formData, { abortEarly: false });
            }
            setErrors({});
            return true;
        } catch (error) {
            const newErrors: any = {};
            // If validation fails on submit, then errors are shown
            if (error instanceof Yup.ValidationError) {
                error.inner.forEach((err) => {
                    newErrors[err.path as keyof typeof formData] = err.message;
                });
                setErrors(newErrors);
            }

            // Focus on the first field with an error
            if (Object.keys(newErrors).length > 0) {
                const firstErrorField = Object.keys(newErrors)[0];
                const element = refs.current[firstErrorField];
                if (element) {
                    element.focus();
                }
            }
            return false
        }
    };

    const onChangeAction = (value: boolean) => {

        if (edit) {
            if (value === true) {
                locationData.address1 = '';
            }
            setLocationData({ ...locationData, hasDefaultAddress: value })
            onDataChange({ ...locationData, hasDefaultAddress: value })

        } else {
            const newFormData = {
                ...formData,
                'hasDefaultAddress': value,
                ...(value ? {
                    country: company.country || formData.country,
                    state: company.state || formData.state,
                } : {}),
            };
            setFormData(newFormData);
            onDataChange(newFormData);
        }

    };

    const handleComboWashesChange = (value: boolean) => {
        if (edit) {
            const newLocationData = {
                ...locationData,
                isComboWash: value,
            };
            setLocationData(newLocationData);
            onDataChange(newLocationData);
        } else {
            const newFormData = {
                ...formData,
                isComboWash: value,
            };
            setFormData(newFormData);
            onDataChange(newFormData);
        }
    };

    const handleFeatureToggle = (field: 'allowGiftCardManagement' | 'blockDiscountedGiftCards' | 'allowLoyaltyPointsManagement', value: boolean) => {
        if (edit) {
            const newLocationData = {
                ...locationData,
                [field]: value,
            };
            setLocationData(newLocationData);
            onDataChange(newLocationData);
        } else {
            const newFormData = {
                ...formData,
                [field]: value,
            };
            setFormData(newFormData);
            onDataChange(newFormData);
        }
    };

    const applyFleetFormChange = (updates: Partial<LocationData>) => {
        if (edit) {
            const newLocationData = {
                ...locationData,
                ...updates,
            };
            setLocationData(newLocationData);
            onDataChange(newLocationData);
        } else {
            const newFormData = {
                ...formData,
                ...updates,
            };
            setFormData(newFormData);
            onDataChange(newFormData);
        }
    };

    const clearFleetFieldErrors = (...fieldNames: string[]) => {
        const hasFieldError = fieldNames.some((fieldName) => errors[fieldName]);
        if (!hasFieldError) {
            return;
        }
        const nextErrors = { ...errors };
        fieldNames.forEach((fieldName) => {
            nextErrors[fieldName] = '';
        });
        setErrors(nextErrors);
    };

    const handleFleetDiscountToggle = (enabled: boolean) => {
        applyFleetFormChange(enabled
            ? { enableFleetDiscount: true, fleetPaymentMode: FleetPaymentMode.CreditCard }
            : getDisabledFleetSettings());
        clearFleetFieldErrors(
            'enableFleetDiscount',
            'fleetDiscount',
            'fleetPaymentMode'
        );
    };

    const handleFleetDiscountChange = (value: string) => {
        applyFleetFormChange({ fleetDiscount: value });
        clearFleetFieldErrors('fleetDiscount');
    };

    const handleFleetPaymentModeChange = (mode: number) => {
        applyFleetFormChange({ fleetPaymentMode: mode });
        clearFleetFieldErrors('fleetPaymentMode');
    };

    // Triggers whenever options is selected
    const handleSelectChange = (selectedOption: any, name: string) => {
        const value = selectedOption ? selectedOption.value : '';

        // To remove validation when user enters input
        const keys = Object.keys(errors);
        if (keys.includes(name)) {
            setErrors({
                ...errors,
                [name]: ""
            })
        }

        if (name === 'country') {
            setErrors({
                ...errors,
                country: '',
                state: '',
                zipcode: '',
            });
            if (edit) {
                const newLocationData = {
                    ...locationData,
                    country: value,
                    state: '',
                    zipcode: '',
                };
                setLocationData(newLocationData);
                onDataChange(newLocationData);
            } else {
                const newFormData = {
                    ...formData,
                    country: value,
                    state: '',
                    zipcode: '',
                };
                setFormData(newFormData);
                onDataChange(newFormData);
            }
            getSateList(value);
            return;
        }

        if (edit) {
            setLocationData({
                ...locationData,
                [name]: value
            })

            onDataChange({
                ...locationData,
                [name]: value
            });
        } else {
            const newFormData = {
                ...formData,
                [name]: value,
            };
            setFormData(newFormData);
            onDataChange(newFormData);
            if (name === 'state') {
                const element = refs.current['zipcode'];
                if (element) {
                    element.focus();
                }
            }
        }


    };

    // On change of radio button
    const onUseAddressChange = (useAddress: boolean) => {
        if (edit) {
            setLocationData({ ...locationData, hasDefaultAddress: useAddress })
        }
        else {
            setFormData({ ...formData, hasDefaultAddress: useAddress })
        }

    }

    // Validate form triggered whenever formdata or locationdata changes
    useEffect(() => {
        onValidate(validateForm);
    }, [formData, onValidate, locationData]);

    useEffect(() => {
        if (m_formData != null) {
            setFormData(m_formData ? m_formData : formData);
        }
    }, [m_formData]);


    return (
        <div className="form-inner mx-auto">
            <CWInput
                id="locationName"
                name="locationName"
                type="text"
                label="Location Name"
                value={edit ? locationData.locationName : formData.locationName}
                onChange={handleChange}
                errorMessage={errors.locationName}
                maxLength={LOCATION_NAME_MAX_LENGTH}
                isMandatory
                ref={(el) => (refs.current.locationname = el)}
                autoFocus
            />
            <CWInput
                id="email"
                name="email"
                type="text"
                label="Email Address"
                value={edit ? locationData.email : formData.email}
                onChange={handleChange}
                errorMessage={errors.email}
                maxLength={EMAIL_MAX_LENGTH}
                ref={(el) => (refs.current.email = el)}
                isMandatory
            />
            <CWSelect
                id="timezoneOffset"
                name="timezoneOffset"
                options={TIMEZONE_OFFSET_OPTIONS}
                values={TIMEZONE_OFFSET_OPTIONS.filter(option => option.value === (edit ? locationData.timezoneOffset : formData.timezoneOffset))}
                value={edit ? locationData.timezoneOffset : formData.timezoneOffset}
                label="Timezone"
                dropdownGap={0}
                dropdownPosition="auto"
                placeholder="Select"
                multi={false}
                searchable={true}
                isMandatory
                errorMessage={errors.timezoneOffset}
                onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'timezoneOffset')}
                className="no-input"
            />
            <div className="car-info-sec d-flex flex-column align-items-start mb-4">
                <div className="info-switch d-flex align-items-center gap-3">
                    <h5 className="f-bold m-0">Allow Combo Washes</h5>
                    <CWSwitch
                        id="isComboWash"
                        name="isComboWash"
                        checked={edit ? locationData.isComboWash : formData.isComboWash}
                        onChange={() => handleComboWashesChange(!(edit ? locationData.isComboWash : formData.isComboWash))}
                    />
                </div>
            </div>
            <div className="adderss-sec">
                <h5 className='f-bold'>Address</h5>
                <div className="radio-field-group d-flex">
                    <CWRadio
                        id="address1"
                        name="hasDefaultAddress"
                        label="Use Car Wash Address"
                        checked={edit ? locationData.hasDefaultAddress : formData.hasDefaultAddress}
                        onChange={() => onChangeAction(true)}

                    />
                    <CWRadio
                        id="newaddress"
                        name="hasDefaultAddress"
                        label="Add New Address"
                        checked={edit ? !locationData.hasDefaultAddress : !formData.hasDefaultAddress}
                        onChange={() => onChangeAction(false)}
                    />
                </div>

                {formData.hasDefaultAddress && !edit ?
                    (<div className="address mb-4">
                        <p className='f-medium'>
                            {company.companyName && `${company.companyName}, `}
                            {company.address1 && `${company.address1}`}<br />
                            {company.city && `${company.city}, `}
                            {company.state && `${company.state} `}
                            {company.zipCode && company.zipCode}
                            {companyCountryName && `${company.city || company.state || company.zipCode ? ', ' : ''}${companyCountryName}`}
                        </p>
                    </div>
                    ) :
                    edit && locationData.hasDefaultAddress ? (
                        <div className="address mb-4">
                            <p className='f-medium'>{locationData.address1},<br />
                                {locationData.city},{locationData.zipcode}
                                {locationCountryName && `, ${locationCountryName}`}
                            </p>
                        </div>

                    ) : null
                }
                {((!formData.hasDefaultAddress) || (!locationData.hasDefaultAddress)) ? (

                    <>
                        <Row>
                            <Col md={6}>
                                <CWInput
                                    id="address1"
                                    name="address1"
                                    type="text"
                                    value={edit ? locationData.address1 : formData.address1}
                                    label="Address 1"
                                    onChange={handleChange}
                                    errorMessage={errors.address1}
                                    maxLength={WASH_ADDRESS_MAX_LENGTH}
                                    ref={(el) => (refs.current.washaddress = el)}
                                    isMandatory
                                />
                            </Col>
                            <Col md={6}>
                                <CWInput
                                    id="address"
                                    name="address2"
                                    value={edit ? locationData.address2 : formData.address2}
                                    maxLength={WASH_ADDRESS_MAX_LENGTH}
                                    type="text"
                                    label="Address 2"
                                    labelSuffix="(Optional)"
                                    errorMessage={errors.address2}
                                    onChange={handleChange}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <CWInput
                                    id="city"
                                    name="city"
                                    type="text"
                                    label="City"
                                    value={edit ? locationData.city : formData.city}
                                    maxLength={CITY_MAX_LENGTH}
                                    isMandatory
                                    ref={(el) => (refs.current.city = el)}
                                    errorMessage={errors.city}
                                    onChange={handleChange}
                                />
                            </Col>
                            <Col md={6}>
                                <CWSelect
                                    id="country"
                                    name="country"
                                    options={countryOptions}
                                    values={countryOptions.filter(option => option.value === (edit ? locationData.country : formData.country))}
                                    label="Country"
                                    dropdownGap={0}
                                    dropdownPosition="auto"
                                    placeholder="Select"
                                    multi={false}
                                    searchable={false}
                                    disabled
                                    isMandatory
                                    errorMessage={errors.country}
                                    onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'country')}
                                    className="no-input"
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col md={6}>
                                <CWSelect
                                    id="state"
                                    name="state"
                                    options={options}
                                    values={edit
                                        ? options.filter(option => option.value === locationData.state || option.label === locationData.state)
                                        : options.filter(option => option.value === formData.state)}
                                    value={edit ? locationData.state : formData.state}
                                    label={stateLabel}
                                    dropdownGap={0}
                                    dropdownPosition="auto"
                                    placeholder="Select"
                                    multi={false}
                                    searchable={false}
                                    isMandatory
                                    errorMessage={errors.state}
                                    onChange={(selectedOptions) => handleSelectChange(selectedOptions[0], 'state')}
                                    className="no-input"
                                />
                            </Col>
                            <Col md={6}>
                                <CWInput
                                    id="zipcode"
                                    name="zipcode"
                                    type="text"
                                    value={edit ? locationData.zipcode : formData.zipcode}
                                    label={postalLabel}
                                    isMandatory
                                    maxLength={postalMaxLength}
                                    errorMessage={errors.zipcode}
                                    ref={(el) => (refs.current.zipcode = el)}
                                    onChange={handleChange}
                                />
                            </Col>
                        </Row>
                    </>
                ) : null}
            </div>
            <LocationFleetSettings
                enableFleetDiscount={Boolean(edit ? locationData.enableFleetDiscount : formData.enableFleetDiscount)}
                fleetDiscount={edit ? (locationData.fleetDiscount || '') : (formData.fleetDiscount || '')}
                fleetPaymentMode={edit ? (locationData.fleetPaymentMode ?? null) : (formData.fleetPaymentMode ?? null)}
                errors={errors}
                onToggle={handleFleetDiscountToggle}
                onDiscountChange={handleFleetDiscountChange}
                onPaymentModeChange={handleFleetPaymentModeChange}
                refs={refs}
            />
            <LocationFeatureToggles
                allowGiftCardManagement={Boolean(edit ? locationData.allowGiftCardManagement : formData.allowGiftCardManagement)}
                blockDiscountedGiftCards={Boolean(edit ? locationData.blockDiscountedGiftCards : formData.blockDiscountedGiftCards)}
                allowLoyaltyPointsManagement={Boolean(edit ? locationData.allowLoyaltyPointsManagement : formData.allowLoyaltyPointsManagement)}
                onGiftCardChange={(enabled) => handleFeatureToggle('allowGiftCardManagement', enabled)}
                onBlockDiscountedGiftCardsChange={(enabled) => handleFeatureToggle('blockDiscountedGiftCards', enabled)}
                onLoyaltyChange={(enabled) => handleFeatureToggle('allowLoyaltyPointsManagement', enabled)}
            />
        </div>
    );
};
export default LocationInfoForm;
